import React from 'react'
import { Box, Typography, keyframes } from '@mui/material'
import { AccountTree as NetworkIcon } from '@mui/icons-material'

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`

const bounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-12px);
  }
`

function Preloader({ darkMode = false }) {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: darkMode
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        zIndex: 9999,
        transition: 'opacity 0.5s ease-out',
      }}
    >
      {/* Animated Background Gradient */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '400px',
          height: '400px',
          background: darkMode
            ? 'radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(59,130,246,0.05) 0%, transparent 70%)',
          animation: `${pulse} 3s ease-in-out infinite`,
          pointerEvents: 'none',
        }}
      />

      {/* Main Content Container */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 2,
        }}
      >
        {/* Logo Box */}
        <Box
          sx={{
            width: 70,
            height: 70,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 10px 30px rgba(59,130,246,0.3)',
            animation: `${pulse} 2s ease-in-out infinite`,
            mb: 4,
            flexShrink: 0,
          }}
        >
          <NetworkIcon sx={{ fontSize: 38, color: 'white' }} />
        </Box>

        {/* Brand Name with Shimmer */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
            letterSpacing: '-0.5px',
            animation: `${fadeIn} 1s ease-out 0.3s both`,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              animation: `${shimmer} 2.5s infinite`,
            },
          }}
        >
          SupplyNet Web
        </Typography>

        {/* Tagline */}
        <Typography
          variant="body2"
          sx={{
            color: darkMode ? '#94a3b8' : '#64748b',
            fontWeight: 500,
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            fontSize: '0.65rem',
            animation: `${fadeIn} 1s ease-out 0.5s both`,
            mb: 3,
          }}
        >
          Powered by SupplyNetPy
        </Typography>

        {/* Bouncing Dots */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.2,
            animation: `${fadeIn} 1s ease-out 0.7s both`,
            alignItems: 'center',
          }}
        >
          {[0, 1, 2, 3, 4].map(i => (
            <Box
              key={i}
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                boxShadow: '0 2px 8px rgba(59,130,246,0.4)',
                animation: `${bounce} 1.4s ease-in-out infinite`,
                animationDelay: `${i * 0.15}s`,
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default Preloader